import { DeleteOutlined, UserAddOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectPeople } from 'reduxStore/people/selectors';
import { selectTeams } from 'reduxStore/teams/selectors';
import { teamsActions } from 'reduxStore/teams/slice';
import { Button, Col, Flex, List, Modal, Row, Select, Tag, Typography } from 'zui/Antd';
import { ConditionalTooltipButton } from 'zui/Custom';

const { Title, Text } = Typography;

const AddPeopleToTeamsModal = ({ children, personIDs, setSelectedPersonIDs }) => {
	// ✅ Hooks
	const dispatch = useDispatch();
	const {
		token: {
			fontSizeSM,
			marginLG,
			paddingXL,
			padding,
			paddingLG,
			paddingXS,
			paddingMD,
			colorTextDescription,
			colorText,
			paddingSM
		}
	} = theme.useToken();

	// ✅ Redux State: Get all teams
	const people = useSelector(selectPeople);
	const teams = useSelector(selectTeams);
	const { loadingAddMembersTeam, completeAddMembersTeam } = useSelector((state) => state.Teams);

	// ✅ Local State
	const [visible, setVisible] = useState(false);
	const [selectedTeams, setSelectedTeams] = useState([]);
	const [showAllSelected, setShowAllSelected] = useState(false);

	// ✅ Close modal
	const handleCancel = useCallback(() => {
		setSelectedTeams([]);
		setSelectedPersonIDs([]);
		setVisible(false);
	}, [setSelectedPersonIDs]);

	// ✅ Close modal when adding people to teams is successful
	useEffect(() => {
		if (completeAddMembersTeam) {
			handleCancel();
		}
	}, [completeAddMembersTeam, handleCancel]);

	// ✅ Handle adding people to selected teams
	const handleAddPeopleToTeams = useCallback(() => {
		if (selectedTeams.length > 0 && personIDs.length > 0) {
			selectedTeams.forEach((team) => {
				dispatch(
					teamsActions.addTeamMember({
						teamID: team.teamID,
						personIDs
					})
				);
			});
		}
	}, [dispatch, selectedTeams, personIDs]);

	// ✅ Handle people selection
	const handleSelectPeople = (personID) => {
		const selectedPerson = people?.find((person) => person.personID === personID);
		if (selectedPerson && !personIDs.some((id) => id === personID)) {
			setSelectedPersonIDs([...personIDs, selectedPerson.personID]);
		}
	};

	// ✅ Handle team selection
	const handleSelectTeam = (teamID) => {
		const selectedTeam = teams?.find((team) => team.teamID === teamID);
		if (selectedTeam && !selectedTeams.some((t) => t.teamID === teamID)) {
			setSelectedTeams([...selectedTeams, selectedTeam]);
		}
	};

	// ✅ Handle removing a team
	const handleRemoveTeam = (teamID) => {
		setSelectedTeams(selectedTeams.filter((team) => team.teamID !== teamID));
	};

	// ✅ Handle removing a person
	const handleRemovePerson = (personID) => {
		setSelectedPersonIDs(personIDs.filter((id) => id !== personID));
	};

	// ✅ Helper to pluralize label
	const pluralizeLabel = (label) => {
		let pluralLabel = label;

		if (personIDs.length > 1) {
			pluralLabel = pluralLabel
				.replace(/person/gi, (match) => (match === match.toLowerCase() ? 'people' : 'People'))
				.replace(/profile/gi, (match) => (match === match.toLowerCase() ? 'profiles' : 'Profiles'));
		}

		if (selectedTeams.length > 1) {
			pluralLabel = pluralLabel.replace(/team/gi, (match) => (match === match.toLowerCase() ? 'teams' : 'Teams'));
		}

		return pluralLabel;
	};

	return (
		<Fragment>
			{/* 🔥 Trigger Button */}
			<ConditionalTooltipButton
				buttonText={pluralizeLabel('Add Person')}
				condition={false}
				disabledCondition={false}
				trueTitle={"You don't have permission"}
				falseTitle={"Add this Person's to Teams"}
				onClick={() => setVisible(true)}
				icon={<UserAddOutlined />}
			>
				{children}
			</ConditionalTooltipButton>

			{/* 🔥 Modal */}
			<Modal
				open={visible}
				onCancel={handleCancel}
				closeIcon={false}
				centered
				destroyOnClose
				footer={[
					<Flex
						align='center'
						justify='flex-end'
						style={{ marginTop: marginLG }}
						gap={paddingSM}
						key='footer'
					>
						<Button onClick={handleCancel} type='default' disabled={loadingAddMembersTeam}>
							Cancel
						</Button>
						<Button
							onClick={handleAddPeopleToTeams}
							type='primary'
							loading={loadingAddMembersTeam}
							disabled={selectedTeams.length === 0 || personIDs.length === 0}
						>
							{pluralizeLabel('Add Person')}
						</Button>
					</Flex>
				]}
				width={472}
				styles={{ content: { padding: paddingXL } }}
			>
				{/* ✅ Modal Content */}
				<Row gutter={[paddingLG, paddingLG]} align='middle'>
					<Col span={24}>
						<Title level={4} style={{ margin: 0 }}>
							{pluralizeLabel('Add Person to Team')}
						</Title>
						<Text style={{ fontSize: fontSizeSM, color: colorTextDescription }}>
							Assigning {pluralizeLabel('person')} to a {pluralizeLabel('team')} grants them all the
							permissions <br />
							associated with that {pluralizeLabel('team')}.
						</Text>
					</Col>

					<Col span={24}>
						<Flex vertical gap={padding}>
							<Text strong>People</Text>
							<Select
								showSearch
								placeholder={pluralizeLabel('Enter or Select Person Name')}
								style={{ width: '100%' }}
								options={people
									.filter(
										(person) => !personIDs.includes(person.personID) // ✅ Filter out selected people
									)
									.map((person) => ({
										label:
											person.firstName && person.lastName
												? `${person.firstName} ${person.lastName}`
												: person.displayName
												? person.displayName
												: person.email,
										value: person.personID
									}))}
								onSelect={handleSelectPeople}
								filterOption={(input, option) =>
									option.label.toLowerCase().includes(input.toLowerCase())
								}
								value={null}
							/>
						</Flex>
					</Col>

					{/* 🔥 Selected People */}
					<Col span={24}>
						<Text strong>Selected People</Text>

						<Flex wrap gap={8} style={{ marginTop: paddingXS }} align='center'>
							{(showAllSelected ? personIDs : personIDs.slice(0, 5)).map((personID) => {
								const person = people?.find((p) => p.personID === personID);
								return person ? (
									<Tag
										key={personID}
										closable
										onClose={() => handleRemovePerson(personID)}
										style={{ fontSize: fontSizeSM, margin: 0 }}
									>
										{person.firstName && person.lastName
											? `${person.firstName} ${person.lastName}`
											: person.email}
									</Tag>
								) : null;
							})}

							{/* Show remaining selected people count if more than 5 are selected */}
							{personIDs.length > 5 && !showAllSelected && (
								<Tag
									style={{
										fontSize: fontSizeSM,
										margin: 0
									}}
								>
									+{personIDs.length - 5} more
								</Tag>
							)}

							{/* Button for Expand/Collapse */}
							{personIDs.length > 5 && (
								<Button
									type='link'
									onClick={() => setShowAllSelected(!showAllSelected)}
									style={{ padding: 0, fontSize: fontSizeSM, marginL: 0, height: 20 }}
								>
									{showAllSelected ? 'Collapse' : 'Expand'}
								</Button>
							)}
						</Flex>
					</Col>

					{/* 🔥 Selected Teams */}
					<Col span={24}>
						<Flex vertical gap={padding}>
							<Text strong>Teams</Text>
							<Select
								showSearch
								placeholder='Enter or Select Team Name'
								style={{ width: '100%' }}
								options={teams
									.filter(
										(team) => !selectedTeams.some((t) => t.teamID === team.teamID) // ✅ FIX aquí
									)
									.map((team) => ({
										label: team.name,
										value: team.teamID
									}))}
								onSelect={handleSelectTeam} // ✅ When a user selects a team
								filterOption={(input, option) =>
									option.label.toLowerCase().includes(input.toLowerCase())
								}
								value={null}
							/>
						</Flex>
					</Col>

					{/* 🔥 Render Selected Teams */}
					<Col span={24}>
						<Flex vertical gap={paddingMD}>
							<Flex align='center' justify='space-between'>
								<Text strong>Selected Teams</Text>
								<Button
									type='text'
									onClick={() => setSelectedTeams([])}
									style={{ textDecoration: 'underline' }}
								>
									Clear List
								</Button>
							</Flex>

							<List
								dataSource={selectedTeams}
								loading={loadingAddMembersTeam}
								style={{
									minHeight: 186
								}}
								renderItem={(team) => (
									<List.Item
										style={{ padding: 0, border: 0, marginBottom: paddingXS }}
										extra={
											<Button
												icon={<DeleteOutlined style={{ fontSize: 16 }} />}
												onClick={() => handleRemoveTeam(team.teamID)}
												type='text'
												style={{ color: colorText }}
											/>
										}
									>
										<List.Item.Meta
											avatar={null}
											title={<Text style={{ fontWeight: 400 }}>{team.name}</Text>}
										/>
									</List.Item>
								)}
							/>
						</Flex>
					</Col>
				</Row>
			</Modal>
		</Fragment>
	);
};

export default AddPeopleToTeamsModal;
